// =============================================================================
// Investor Service
// =============================================================================
// API client for investor portal functionality
// =============================================================================

import api from './api';
import type {
  DashboardStats,
  Announcement,
} from '../types';

// =============================================================================
// Types
// =============================================================================

export interface InvestorDashboardResponse {
  stats: DashboardStats;
}

export interface InvestmentListItem {
  deal: {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    propertyType: string;
    totalEquityRaised: number;
    acquisitionDate?: string;
    isActive: boolean;
    imageUrl?: string;
  };
  profile: {
    id: string;
    type: string;
    legalName?: string;
    entityName?: string;
  };
  investmentId: string;
  investmentAmount: number;
  ownershipPercentage: number;
  totalDistributed: number;
}

export interface InvestmentDetailResponse {
  deal: {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    propertyType: string;
    totalEquityRaised: number;
    acquisitionDate?: string;
    description?: string;
    isActive: boolean;
    imageUrl?: string;
  };
  investmentAmount: number;
  ownershipPercentage: number;
  totalDistributed: number;
  allocations: {
    id: string;
    amount: number;
    distribution: {
      id: string;
      type: string;
      distributionDate: string;
      totalAmount: number;
      notes?: string;
      paymentMethod: string;
    };
  }[];
  documents: {
    id: string;
    type: string;
    name: string;
    description?: string;
    createdAt: string;
  }[];
}

export interface DistributionItem {
  id: string;
  amount: number;
  distribution: {
    id: string;
    dealId: string;
    deal: {
      id: string;
      name: string;
      city: string;
      state: string;
    };
    totalAmount: number;
    distributionDate: string;
    type: string;
    paymentMethod: string;
    notes?: string;
  };
  profile: {
    id: string;
    type: string;
    legalName?: string;
    entityName?: string;
  };
}

export interface DistributionsResponse {
  distributions: DistributionItem[];
  total: number;
}

export interface DocumentItem {
  id: string;
  dealId: string;
  deal: {
    id: string;
    name: string;
  };
  type: string;
  name: string;
  description?: string;
  createdAt: string;
  mimeType?: string;
}

export interface ProfileWithMaskedData {
  id: string;
  type: 'INDIVIDUAL' | 'LLC' | 'TRUST' | 'CORPORATION' | 'PARTNERSHIP';
  legalName?: string;
  entityName?: string;
  authorizedSigner?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  ssnMasked?: string;
  feinMasked?: string;
  bankName?: string;
  bankAccountType?: 'CHECKING' | 'SAVINGS';
  routingNumberMasked?: string;
  accountNumberMasked?: string;
  investments?: {
    id: string;
    dealId: string;
    dealName: string;
    investmentAmount: number;
    ownershipPercentage: number;
  }[];
}

// =============================================================================
// Investor Service
// =============================================================================

export const investorService = {
  // ===========================================================================
  // Dashboard
  // ===========================================================================

  async getDashboard(): Promise<DashboardStats> {
    const response = await api.get<{ success: boolean; data: InvestorDashboardResponse }>(
      '/investor/dashboard'
    );
    return response.data.data.stats;
  },

  // ===========================================================================
  // Investments
  // ===========================================================================

  async getInvestments(): Promise<InvestmentListItem[]> {
    const response = await api.get<{ success: boolean; data: InvestmentListItem[] }>(
      '/investor/investments'
    );
    return response.data.data;
  },

  async getInvestmentDetail(dealId: string): Promise<InvestmentDetailResponse> {
    const response = await api.get<{ success: boolean; data: InvestmentDetailResponse }>(
      `/investor/investments/${dealId}`
    );
    return response.data.data;
  },

  // ===========================================================================
  // Distributions
  // ===========================================================================

  async getDistributions(options?: {
    dealId?: string;
    year?: number;
  }): Promise<DistributionsResponse> {
    const params = new URLSearchParams();
    if (options?.dealId) params.append('dealId', options.dealId);
    if (options?.year) params.append('year', options.year.toString());

    const response = await api.get<{ success: boolean; data: DistributionsResponse }>(
      `/investor/distributions?${params.toString()}`
    );
    return response.data.data;
  },

  // ===========================================================================
  // Documents
  // ===========================================================================

  async getDocuments(options?: {
    dealId?: string;
    type?: string;
  }): Promise<DocumentItem[]> {
    const params = new URLSearchParams();
    if (options?.dealId) params.append('dealId', options.dealId);
    if (options?.type) params.append('type', options.type);

    const response = await api.get<{ success: boolean; data: DocumentItem[] }>(
      `/investor/documents?${params.toString()}`
    );
    return response.data.data;
  },

  async downloadDocument(documentId: string): Promise<Blob> {
    const response = await api.get(`/investor/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getDocumentViewUrl(documentId: string): Promise<{
    embedUrl: string;
    downloadUrl: string;
    previewUrl: string;
    proxyUrl: string;
    fileName: string;
    fileType: string;
    mimeType: string;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        embedUrl: string;
        downloadUrl: string;
        previewUrl: string;
        fileName: string;
        fileType: string;
        mimeType: string;
      };
    }>(`/investor/documents/${documentId}/view-url`);

    // Fetch the file through our backend proxy and create a blob URL
    // This provides secure access to files via the backend
    let proxyUrl = '';
    try {
      const fileResponse = await api.get(`/investor/documents/${documentId}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([fileResponse.data], { type: response.data.data.mimeType });
      proxyUrl = URL.createObjectURL(blob);
    } catch (err) {
      console.error('Failed to fetch document via proxy:', err);
      // Fall back to embed URL if proxy fails
      proxyUrl = response.data.data.embedUrl;
    }

    return {
      ...response.data.data,
      proxyUrl,
    };
  },

  // ===========================================================================
  // Profiles
  // ===========================================================================

  async getProfiles(): Promise<ProfileWithMaskedData[]> {
    const response = await api.get<{ success: boolean; data: ProfileWithMaskedData[] }>(
      '/investor/profiles'
    );
    return response.data.data;
  },

  async revealSensitiveData(
    profileId: string,
    dataType: 'ssn' | 'fein' | 'bank_account' | 'routing_number'
  ): Promise<{ value: string; expiresIn: number }> {
    const response = await api.get<{
      success: boolean;
      data: { value: string; expiresIn: number };
    }>(`/investor/profiles/${profileId}/reveal/${dataType}`);
    return response.data.data;
  },

  async updateProfile(
    profileId: string,
    data: {
      legalName?: string;
      entityName?: string;
      authorizedSigner?: string;
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    }
  ): Promise<ProfileWithMaskedData> {
    const response = await api.put<{ success: boolean; data: ProfileWithMaskedData }>(
      `/investor/profiles/${profileId}`,
      data
    );
    return response.data.data;
  },

  async updateProfileSensitive(
    profileId: string,
    data: {
      ssn?: string;
      fein?: string;
      bankName?: string;
      bankAccountType?: 'CHECKING' | 'SAVINGS';
      routingNumber?: string;
      accountNumber?: string;
    }
  ): Promise<ProfileWithMaskedData> {
    const response = await api.put<{ success: boolean; data: ProfileWithMaskedData }>(
      `/investor/profiles/${profileId}/sensitive`,
      data
    );
    return response.data.data;
  },

  // ===========================================================================
  // Announcements
  // ===========================================================================

  async getAnnouncements(): Promise<Announcement[]> {
    const response = await api.get<{ success: boolean; data: Announcement[] }>(
      '/investor/announcements'
    );
    return response.data.data;
  },

  async markAnnouncementRead(announcementId: string): Promise<void> {
    await api.put(`/investor/announcements/${announcementId}/read`);
  },

  // ===========================================================================
  // Messages
  // ===========================================================================

  async getMessages(filter?: 'all' | 'unread' | 'archived'): Promise<{
    id: string;
    subject: string;
    body: string;
    fromUser?: { name: string; isAdmin: boolean };
    isSentByMe: boolean;
    readAt?: string;
    createdAt: string;
  }[]> {
    const params = filter ? `?filter=${filter}` : '';
    const response = await api.get<{ success: boolean; data: {
      id: string;
      subject: string;
      body: string;
      fromUser?: { name: string; isAdmin: boolean };
      isSentByMe: boolean;
      readAt?: string;
      createdAt: string;
    }[] }>(`/investor/messages${params}`);
    return response.data.data;
  },

  async markMessageRead(messageId: string): Promise<void> {
    await api.put(`/investor/messages/${messageId}/read`);
  },

  // ===========================================================================
  // Notification Counts
  // ===========================================================================

  async getUnreadCounts(): Promise<{ announcements: number; messages: number }> {
    // Fetch both in parallel
    const [announcements, messages] = await Promise.all([
      this.getAnnouncements(),
      this.getMessages('all'),
    ]);

    return {
      announcements: announcements.filter(a => !a.readAt).length,
      messages: messages.filter(m => !m.readAt && !m.isSentByMe).length,
    };
  },

  // ===========================================================================
  // Export
  // ===========================================================================

  async exportWaterfall(dealId: string): Promise<void> {
    const response = await api.get(`/investor/investments/${dealId}/export-waterfall?t=${Date.now()}`, {
      responseType: 'blob',
    });

    // Create blob URL and trigger download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Extract filename from content-disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = `Waterfall_${new Date().toISOString().split('T')[0]}.xlsx`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export default investorService;
